import * as THREE from 'three';
import { INPUT_EDGE_MARGIN } from '../handlers/InputHandler';
import { keyCodes } from '../handlers/KeyboardHandler';
import { createAngleMeshTextObject } from '../helpers/AngleHelpers';
import { parseNumberString, roundNumber } from '../helpers/NumberHelpers';
import { disposeObject } from '../helpers/ObjectHelpers';
import { objToCoords } from '../helpers/WorldHelpers';
import { Window } from './Window';
import {
  getBool,
  getNumber,
  getNumberOrNull,
  getObjectNumbersInThousands,
} from './WindowHelpers';

export class PointyTopWindow extends Window {
  constructor(renderer, parent, def) {
    super(renderer, parent, def);
  }

  _parseGeometryData(geometryData) {
    return {
      H1: getNumber(geometryData, 'H1', 'Length') / 1000,
      H2: getNumber(geometryData, 'H2', 'Length') / 1000,
      W1: getNumber(geometryData, 'W1', 'Length') / 1000,
    };
  }

  _create2DVertices(width, height, adjust, geometryData) {
    return create2DVertices(
      width,
      height,
      adjust,
      geometryData.H1,
      geometryData.H2,
      geometryData.W1
    );
  }

  _transformDimensionsToRect(dimensions, width, height, geometryData) {
    return transformDimensionsToRect(
      dimensions,
      width,
      height,
      geometryData.H1,
      geometryData.H2,
      geometryData.W1
    );
  }

  _getGeometryChangeOnShrink(geometryData, dimensions, width, height) {
    const angleLeft1 = Math.atan2(geometryData.H1, geometryData.W1);
    const angleRight1 = Math.atan2(geometryData.H2, width - geometryData.W1);
    const angleTopSub = angleLeft1 + angleRight1 - Math.PI / 2;
    const OKA = dimensions.HSP; // Top left
    const OKB = dimensions.HSP; // Top right

    let dh1 = 0;
    dh1 -= geometryData.H1 * (dimensions.VSP / geometryData.W1);
    dh1 -= (OKA / Math.cos(angleTopSub)) * Math.sin(angleRight1);
    dh1 += OKA / Math.cos(angleLeft1);
    dh1 -= (OKB / Math.cos(angleTopSub)) * Math.sin(angleLeft1);

    let dh2 = 0;
    dh2 -= geometryData.H2 * (dimensions.HSP / (width - geometryData.W1));
    dh2 -= (OKB / Math.cos(angleTopSub)) * Math.sin(angleLeft1);
    dh2 += OKB / Math.cos(angleRight1);
    dh2 -= (OKA / Math.cos(angleTopSub)) * Math.sin(angleRight1);

    return {
      H1: dh1,
      H2: dh2,
      W1:
        OKA / Math.cos(angleTopSub) -
        dimensions.VSP -
        OKB / Math.cos(angleTopSub),
    };
  }

  _addMovableHeight(container, def, data) {
    const outerDimensions = getObjectNumbersInThousands(
      def,
      'OuterMeasurements'
    );
    const rectChange = this._transformDimensionsToRect(
      outerDimensions,
      data.w,
      data.h,
      data.geometryData
    );
    const geometryDataChange = this._getGeometryChangeOnShrink(
      data.geometryData,
      outerDimensions,
      data.w,
      data.h
    );

    const angleSymbol = '\u00B0';
    const angleObjects = new THREE.Object3D();

    const vsp = outerDimensions.VSP;
    const hsp = outerDimensions.HSP;
    const width = data.w;
    const height = data.h;
    const depth = data.b;
    const h1 = data.geometryData.H1;
    const h2 = data.geometryData.H2;

    const shrinkedH1 = data.geometryData.H1 + geometryDataChange.H1;
    const shrinkedH2 = data.geometryData.H2 + geometryDataChange.H2;
    const shrinkedTop = data.top + rectChange.top;
    const shrinkedBottom = data.bottom - rectChange.bottom;
    const shrinkedLeft = data.left + rectChange.left;
    const shrinkedRight = data.right - rectChange.right;
    const shrinkedWidth = shrinkedRight - shrinkedLeft;
    const shrinkedHeight = shrinkedBottom - shrinkedTop;

    const meshHeight1 = shrinkedHeight - shrinkedH1;
    const meshHeight2 = shrinkedHeight - shrinkedH2;
    const topLeftAngle = Math.PI / 2 - Math.atan2(width / 2, h1);
    const topRightAngle = Math.PI / 2 - Math.atan2(width / 2, h2);
    const angleString = roundNumber(THREE.Math.radToDeg(topLeftAngle), 1)
      .toString()
      .replace('.', ',');

    const defaultMaterial = new THREE.MeshBasicMaterial({
      visible: false,
    });
    const hoverMaterial = new THREE.MeshBasicMaterial({
      color: 0xffe032,
    });
    const selectMaterial = new THREE.MeshBasicMaterial({
      color: 0xd34030,
    });

    const objectContainer = new THREE.Object3D();
    objectContainer.selectable = true;

    const geometry1 = new THREE.BoxGeometry(vsp, meshHeight1, depth);
    const mesh1 = new THREE.Mesh(geometry1, defaultMaterial);
    mesh1.position.set(
      (vsp - width) / 2,
      (-height + meshHeight1) / 2 + outerDimensions.UNP,
      0
    );
    mesh1.scale.addScalar(1e-3); // To resolve Z-fighting

    const geometry2 = new THREE.BoxGeometry(hsp, meshHeight2, depth);
    const mesh2 = new THREE.Mesh(geometry2, defaultMaterial);
    mesh2.position.set(
      (width - hsp) / 2,
      (-height + meshHeight2) / 2 + outerDimensions.UNP,
      0
    );
    mesh2.scale.addScalar(1e-3); // To resolve Z-fighting

    let angleObject1, angleObject2;
    const rebuildAngleTextObjects = () => {
      if (angleObject1) {
        angleObjects.remove(angleObject1);
        disposeObject(angleObject1);
      }
      if (angleObject2) {
        angleObjects.remove(angleObject2);
        disposeObject(angleObject2);
      }

      const camDist =
        this.parent.imageEngine && this.parent.imageEngine.camera
          ? this.parent.imageEngine.camera.getCameraDistance()
          : 1.5;
      const rendererHeightModifier =
        this.parent.imageEngine && this.parent.imageEngine.renderer
          ? 800 /
            this.parent.imageEngine.renderer.getSize(new THREE.Vector2()).height
          : 1;
      const fontSize = 20 * rendererHeightModifier * camDist;

      angleObject1 = createAngleMeshTextObject(
        0,
        topLeftAngle,
        selectMaterial,
        fontSize
      );
      angleObject1.position.set(
        -shrinkedWidth / 2,
        -height / 2 + outerDimensions.UNP + shrinkedHeight - shrinkedH1,
        depth / 2 + 1e-3
      );

      angleObject2 = createAngleMeshTextObject(
        Math.PI - topRightAngle,
        topRightAngle,
        selectMaterial,
        fontSize
      );
      angleObject2.position.set(
        shrinkedWidth / 2,
        -height / 2 + outerDimensions.UNP + shrinkedHeight - shrinkedH2,
        depth / 2 + 1e-3
      );

      angleObjects.add(angleObject1);
      angleObjects.add(angleObject2);
    };
    rebuildAngleTextObjects();
    this.onCameraZoomEvents.push(() => {
      rebuildAngleTextObjects();
    });
    this.onRendererResizeEvents.push(() => {
      rebuildAngleTextObjects();
    });

    const angleBaseHeight = 0.01;
    const angleBaseMeshGeo = new THREE.PlaneGeometry(
      shrinkedWidth,
      angleBaseHeight
    );
    const angleBaseMesh = new THREE.Mesh(angleBaseMeshGeo, selectMaterial);
    angleBaseMesh.position.set(
      0,
      -height / 2 +
        outerDimensions.UNP +
        shrinkedHeight -
        shrinkedH1 -
        angleBaseHeight / 2,
      depth / 2 + 1e-3
    );

    angleObjects.visible = false;
    angleObjects.add(angleBaseMesh);

    objectContainer.add(mesh1);
    objectContainer.add(mesh2);
    objectContainer.add(angleObjects);
    container.add(objectContainer);

    const angleToH = angle => width / 2 / Math.tan(Math.PI * 1.5 - angle);

    const getLimitedHLength = (h => {
      const minAngleDeg = getNumberOrNull(
        this.def,
        'Geometry',
        'H1',
        'MinAngle'
      );
      const minLength =
        getNumberOrNull(this.def, 'Geometry', 'H1', 'MinLength') / 1000;
      const maxLength =
        getNumberOrNull(this.def, 'Geometry', 'H1', 'MaxLength') / 1000;
      if (minLength !== null) {
        h = Math.max(h, minLength);
      }
      if (maxLength !== null) {
        h = Math.min(h, maxLength);
      }
      if (minAngleDeg !== null) {
        const minAngle = THREE.Math.degToRad(minAngleDeg);
        const minLengthH = angleToH(minAngle);
        h = Math.max(h, minLengthH);
      }
      return h;
    }).bind(this);

    objectContainer.data = {
      identifier: def,
    };
    objectContainer.onSelect = () => {
      objectContainer.isSelected = true;

      const xDist = 70;
      const inputContainerMargin = 20;

      const resetHeights = () => {
        const h1 = getNumberOrNull(this.def, 'Geometry', 'H1', 'DefaultLength');
        const h2 = getNumberOrNull(this.def, 'Geometry', 'H2', 'DefaultLength');
        if (h1 !== undefined && h1 !== null) {
          this._setGeometryDataValue('H1', h1);
        }
        if (h2 !== undefined && h2 !== null) {
          this._setGeometryDataValue('H2', h2);
        }
        if (
          (h1 !== undefined && h1 !== null) ||
          (h2 !== undefined && h2 !== null)
        ) {
          this._renderer.facade.rebuild();
        }
      };

      this._renderer.inputHandler.requestInputs({
        inputs: [
          {
            headerText: 'Höjd',
            appendText: `/${roundNumber(height * 1000, 1)}`,
            defaultValue: roundNumber((height - h1) * 1000, 1),
            getCoords: inputContainer => {
              const coords = objToCoords(
                mesh2,
                this._renderer.renderer,
                this._renderer.camera
              );
              const rendererWidth = this._renderer.renderer.getSize(
                new THREE.Vector2()
              ).width;
              const fitsOnRightSide =
                coords.x + xDist + inputContainer.width() + INPUT_EDGE_MARGIN <
                rendererWidth;
              const xPos = fitsOnRightSide
                ? coords.x + xDist
                : coords.x - xDist - inputContainer.width();
              return {
                x: xPos,
                y:
                  coords.y - inputContainer.height() - inputContainerMargin / 2,
              };
            },
            onSubmit: value => {
              this._renderer.scene.updateMatrixWorld();
              const valueNumber = parseNumberString(value) / 1000;
              if (Number.isNaN(valueNumber)) {
                return;
              }

              const newH = getLimitedHLength(height - valueNumber);
              const newValue = roundNumber(newH * 1000, 1);
              this._setGeometryDataValue('H1', newValue);
              this._setGeometryDataValue('H2', newValue);
              this._renderer.facade.rebuild();
              this._renderer.interactionHandler.clearSelectedObjects();
            },
            buttons: [
              {
                side: 'left',
                text: 'Återställ',
                onClick: () => {
                  resetHeights();
                },
              },
              {
                side: 'right',
                text: 'OK',
                onClick: options => options.submit(),
              },
            ],
          },
          {
            headerText: 'Vinkel',
            appendText: angleSymbol,
            defaultValue: angleString,
            getCoords: inputContainer => {
              const coords = objToCoords(
                mesh2,
                this._renderer.renderer,
                this._renderer.camera
              );
              const rendererWidth = this._renderer.renderer.getSize(
                new THREE.Vector2()
              ).width;
              const fitsOnRightSide =
                coords.x + xDist + inputContainer.width() + INPUT_EDGE_MARGIN <
                rendererWidth;
              const xPos = fitsOnRightSide
                ? coords.x + xDist
                : coords.x - xDist - inputContainer.width();
              return {
                x: xPos,
                y: coords.y + inputContainerMargin / 2,
              };
            },
            onSubmit: value => {
              const angle = THREE.Math.degToRad(parseNumberString(value));
              if (Number.isNaN(angle)) {
                return;
              }

              const newH = getLimitedHLength(angleToH(angle));
              const newValue = newH * 1000;
              this._setGeometryDataValue('H1', newValue);
              this._setGeometryDataValue('H2', newValue);
              this._renderer.facade.rebuild();
              this._renderer.interactionHandler.clearSelectedObjects();
            },
            buttons: [
              {
                side: 'left',
                text: 'Återställ',
                onClick: () => {
                  resetHeights();
                },
              },
              {
                side: 'right',
                text: 'OK',
                onClick: options => options.submit(),
              },
            ],
          },
        ],
        onScroll: delta => {
          const dist = 0.01;
          const dir = delta > 0 ? 1 : -1;
          const newH = getLimitedHLength(h1 + dist * dir);
          const newValue = roundNumber(newH * 1000, 1);
          this._setGeometryDataValue('H1', newValue);
          this._setGeometryDataValue('H2', newValue);
          this._renderer.facade.rebuild();
        },
        onInput: which => {
          const dist = 0.01;
          if (which === keyCodes.arrow_up) {
            const newH = getLimitedHLength(h1 - dist);
            const newValue = roundNumber(newH * 1000, 1);
            this._setGeometryDataValue('H1', newValue);
            this._setGeometryDataValue('H2', newValue);
            this._renderer.facade.rebuild();
          } else if (which === keyCodes.arrow_down) {
            const newH = getLimitedHLength(h1 + dist);
            const newValue = roundNumber(newH * 1000, 1);
            this._setGeometryDataValue('H1', newValue);
            this._setGeometryDataValue('H2', newValue);
            this._renderer.facade.rebuild();
          }
        },
      });

      objectContainer.onStateChanged();
    };
    objectContainer.onDeselect = () => {
      objectContainer.isSelected = false;
      objectContainer.onStateChanged();
    };
    objectContainer.onHover = () => {
      objectContainer.isHovered = true;
      objectContainer.onStateChanged();
    };
    objectContainer.onUnhover = () => {
      objectContainer.isHovered = false;
      objectContainer.onStateChanged();
    };
    objectContainer.onStateChanged = () => {
      if (objectContainer.isSelected) {
        mesh1.material = selectMaterial;
        mesh2.material = selectMaterial;
        angleObjects.visible = true;
      } else {
        angleObjects.visible = false;
        if (objectContainer.isHovered) {
          mesh1.material = hoverMaterial;
          mesh2.material = hoverMaterial;
        } else {
          mesh1.material = defaultMaterial;
          mesh2.material = defaultMaterial;
        }
      }
    };

    this.addInteractableObject(objectContainer);
  }

  _onBuildModel(container, def, data) {
    const h1Movable = getBool(this.def, 'Geometry', 'H1', 'IsMovable');
    const h2Movable = getBool(this.def, 'Geometry', 'H2', 'IsMovable');
    if (h1Movable && h2Movable) {
      this._addMovableHeight(container, def, data);
    }
  }
}

export function transformDimensionsToRect(
  dimensions,
  width,
  height,
  reducedHeightLeft,
  reducedHeightRight,
  leftWidth
) {
  const angleLeft1 = Math.atan2(reducedHeightLeft, leftWidth);
  const angleRight1 = Math.atan2(reducedHeightRight, width - leftWidth);
  const angleTopSub = angleLeft1 + angleRight1 - Math.PI / 2;
  const OKA = dimensions.HSP; // Top left
  const OKB = dimensions.HSP; // Top right

  let dTop = 0;
  dTop += (OKA / Math.cos(angleTopSub)) * Math.sin(angleRight1);
  dTop += (OKB / Math.cos(angleTopSub)) * Math.sin(angleLeft1);

  return {
    left: dimensions.VSP,
    right: dimensions.HSP,
    top: dTop,
    bottom: dimensions.UNP,
  };
}

export function create2DVertices(
  width,
  height,
  adjust,
  reducedHeightLeft,
  reducedHeightRight,
  leftWidth
) {
  const hw = width / 2;
  const hh = height / 2;
  const rhl = reducedHeightLeft;
  const rhr = reducedHeightRight;
  const lw = leftWidth;
  const al1 = Math.atan2(reducedHeightLeft, leftWidth);
  const ar1 = Math.atan2(reducedHeightRight, width - leftWidth);
  const ats = al1 + ar1 - Math.PI / 2;
  const a = adjust;
  const OKA = a.HSP; // Top left
  const OKB = a.HSP; // Top right

  const l = -hw - a.VSP; // Left
  const r = hw + a.HSP; // Right
  const cx =
    -hw +
    lw -
    (OKA / Math.cos(ats)) * Math.cos(ar1) +
    (OKB / Math.cos(ats)) * Math.cos(al1); // Center X
  const t =
    hh +
    (OKA / Math.cos(ats)) * Math.sin(ar1) +
    (OKB / Math.cos(ats)) * Math.sin(al1); // Top
  const tl = hh - rhl - a.VSP * Math.tan(al1) + OKA / Math.cos(al1); // Top left
  const tr = hh - rhr - a.HSP * Math.tan(ar1) + OKB / Math.cos(ar1); // Top right
  const b = -hh - a.UNP; // Bottom

  return [cx, t, r, tr, r, b, l, b, l, tl];
}

/*
export function createSolidGeometry(width, height, breadth, innerAdjust, outerAdjust, reducedHeightLeft, reducedHeightRight, leftWidth) {
	const hw = width / 2;
	const hh = height / 2;
	const hb = breadth / 2;
    const rhl = reducedHeightLeft;
    const rhr = reducedHeightRight;
    const lw = leftWidth;
    const al1 = Math.atan2(reducedHeightLeft, leftWidth);
    const ar1 = Math.atan2(reducedHeightRight, width - leftWidth);
    
	const ia = innerAdjust;
	const oa = outerAdjust;
	
    const lo = -hw - oa.VSP; // Left outer
    const ro = hw + oa.HSP; // Right outer
    const cxo = -hw + lw + oa.OKA * Math.cos(ar1) - oa.OKB * Math.cos(al1); // Center X outer
    const to = hh - oa.OKA * Math.sin(ar1) - oa.OKB * Math.sin(al1); // Top outer
    const tlo = hh - rhl + oa.VSP * Math.tan(al1) - oa.OKA / Math.cos(al1); // Top left outer
    const tro = hh - rhr + oa.HSP * Math.tan(ar1) - oa.OKB / Math.cos(ar1); // Top right outer
    const bo = -hh - oa.UNP; // Bottom outer
    
    const li = -hw - ia.VSP; // Left inner
    const ri = hw + ia.HSP; // Right inner
    const cxi = -hw + lw + ia.OKA * Math.cos(ar1) - ia.OKB * Math.cos(al1); // Center X inner
    const ti = hh - ia.OKA * Math.sin(ar1) - ia.OKB * Math.sin(al1); // Top inner
    const tli = hh - rhl + ia.VSP * Math.tan(al1) - ia.OKA / Math.cos(al1); // Top left inner
    const tri = hh - rhr + ia.HSP * Math.tan(ar1) - ia.OKB / Math.cos(ar1); // Top right inner
    const bi = -hh - ia.UNP;// Bottom inner
    
    const v = [
        cxo, to, hb, // Top outer
        ro, tro, hb, // Top right outer
        ro, bo, hb, // Bottom right outer
        lo, bo, hb, // Bottom left outer
        lo, tlo, hb, // Top left outer
        cxi, ti, -hb, // Top inner
        ri, tri, -hb, // Top right inner
        ri, bi, -hb, // Bottom right inner
        li, bi, -hb, // Bottom left inner
        li, tli, -hb, // Top left inner
    ];
    
    const f = [
        0, 2, 1, // Back 1
        0, 3, 2, // Back 2
        0, 4, 3, // Back 3
        5, 6, 7, // Front 1
        5, 7, 8, // Front 2
        5, 8, 9, // Front 3
        0, 1, 5, // Top right 1
        1, 6, 5, // Top right 2
        1, 2, 6, // Right 1
        6, 2, 7, // Right 2
        2, 3, 7, // Bottom 1
        3, 8, 7, // Bottom 2
        3, 4, 8, // Left 1
        4, 9, 8, // Left 2
        4, 0, 9, // Top left 1
        0, 5, 9, // Top left 2
    ];
    
	const g = new THREE.Geometry();
	for (let i = 0; i < v.length; i += 3) {
		g.vertices.push(new THREE.Vector3(v[i], v[i + 1], v[i + 2]));
	}
	for (let i = 0; i < f.length; i += 3) {
		g.faces.push(new THREE.Face3(f[i], f[i + 1], f[i + 2]));
	}
    
    g.computeFaceNormals();
    return g;
}
*/
